import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useBookingStateContext } from "./use-booking-state";
import { BookingFlow, BookingFeature } from "logger/page-tracking";

interface TrackingMetadata {
    flow: BookingFlow;
    feature: BookingFeature;
}

/**
 * Hook to determine current flow and feature for tracking metadata
 *
 * Flow: Determined by route structure (persistent)
 * - simple: /:system/:unitId paths
 * - activity: /:system/:unitId/activity-booking paths
 * - mybooking: /booking/:system/:bookingId paths
 *
 * Feature: Determined by context/state (can change during session)
 * - default: Normal booking flow
 * - request: When in booking request mode
 * - edit: When edit functionality is active
 */
function useTrackingMetadata(metadataFeature?: BookingFeature): TrackingMetadata {
    const location = useLocation();
    const { isBookingRequest } = useBookingStateContext();

    const flow = useMemo((): BookingFlow => {
        const path = location.pathname;

        // MyBooking: /booking/:system/:bookingId
        if (path.startsWith("/booking/")) {
            return BookingFlow.MyBooking;
        }

        // Activity: /:system/:unitId/activity-booking
        if (path.includes("/activity-booking")) {
            return BookingFlow.Activity;
        }

        // Simple: /:system/:unitId (default)
        return BookingFlow.Simple;
    }, [location.pathname]);

    const feature = useMemo((): BookingFeature => {
        // Explicit feature from metadata takes precedence
        if (metadataFeature) {
            return metadataFeature;
        }

        // Request feature from booking state
        if (isBookingRequest) {
            return BookingFeature.Request;
        }

        // Default flow
        return BookingFeature.Default;
    }, [metadataFeature, isBookingRequest]);

    return { flow, feature };
}

export { useTrackingMetadata };
